import type { ReactNode } from "react";
import type { AvatarKey } from "../lib/profiles";

// Match the original Bun and Chicken: soft, slightly asymmetric silhouettes, one pastel
// colour, rounded 2.5px outline features and sparse dark facial details in a 48x48 box.
// Keep the original two drawings unchanged; avoid multicolour patches and shaded details.

const INK = "#172019";
const line = { stroke: INK, strokeWidth: 1.8, strokeLinecap: "round", fill: "none" } as const;
const stroke = { stroke: "currentColor", strokeWidth: 2.5, strokeLinecap: "round", fill: "none" } as const;

export interface AvatarArt {
  label: string;
  fg: string;
  bg: string;
  art: ReactNode;
}

export const AVATAR_ART: Record<AvatarKey, AvatarArt> = {
  bunny: {
    label: "Bunny", fg: "var(--mint)", bg: "#354632",
    art: <>
      <path d="M17 25C10 11 14 3 18 9l5 15M26 24c0-18 8-22 9-14 1 5-3 13-5 16" {...stroke} />
      <path d="M36 32c0 8-6 11-13 11S10 39 10 32s6-11 13-11 13 4 13 11Z" fill="currentColor" />
      <circle cx="19" cy="31" r="1.5" fill={INK} />
      <circle cx="28" cy="31" r="1.5" fill={INK} />
      <path d="m22 35 2 2 2-2" {...line} />
    </>,
  },
  chicken: {
    label: "Chicken", fg: "#f0c08e", bg: "#45382b",
    art: <>
      <path d="M21 16c-7-8 0-11 3-6 3-9 9-6 7 1 7-3 10 3 3 7" {...stroke} />
      <path d="M35 28c0 10-5 15-14 15-7 0-11-5-11-11 0-8 5-16 13-16 7 0 12 4 12 12Z" fill="currentColor" />
      <path d="m33 24 8 5-8 4" fill="currentColor" />
      <circle cx="27" cy="25" r="2" fill="#292019" />
      <path d="M18 31c0 5 5 7 8 4" stroke="#292019" strokeWidth="2.3" strokeLinecap="round" fill="none" />
    </>,
  },
  cow: {
    label: "Cow", fg: "#ece6d8", bg: "#3b3f4a",
    art: <>
      <path d="M17 17c-5-3-7-7-5-10M30 17c5-3 7-7 5-10" {...stroke} />
      <path d="M15 20c-8-8-13-1-5 3l5 1M33 20c8-8 13-1 5 3l-5 1" fill="currentColor" />
      <path d="M36 29c0 9-5 14-12 14S11 38 11 29s4-14 12-14 13 5 13 14Z" fill="currentColor" />
      <circle cx="18" cy="26" r="1.5" fill={INK} /><circle cx="29" cy="26" r="1.5" fill={INK} />
      <path d="M17 35c1-5 13-5 14 0M20 36v1M27 36v1" {...line} />
    </>,
  },
  pig: {
    label: "Pig", fg: "#f3b4c0", bg: "#4a2f38",
    art: <>
      <path d="M14 22C7 16 8 8 12 10l9 8M29 18l8-8c4-2 5 7-2 13" {...stroke} />
      <path d="M38 30c0 8-6 13-14 13S10 39 10 31s6-14 14-14 14 5 14 13Z" fill="currentColor" />
      <circle cx="18" cy="27" r="1.5" fill={INK} /><circle cx="30" cy="27" r="1.5" fill={INK} />
      <path d="M18 35c0-6 12-6 12 0s-12 6-12 0Z" {...line} />
      <path d="M22 34v2M26 34v2" {...line} />
    </>,
  },
  sheep: {
    label: "Sheep", fg: "#eae6dc", bg: "#2f3b4b",
    art: <>
      <path d="M12 25c-8-2-6-10 0-11-1-7 8-10 12-5 5-6 13-2 12 4 8 1 9 10 3 13 3 7-3 12-9 10-4 7-13 5-14-1-7 2-12-5-4-10Z" fill="currentColor" />
      <path d="M16 23c0 13 3 20 8 20s9-7 9-20M17 24l-5 4M32 24l5 4" {...line} />
      <circle cx="21" cy="28" r="1.5" fill={INK} /><circle cx="28" cy="28" r="1.5" fill={INK} />
      <path d="m22 35 2 2 2-2" {...line} />
    </>,
  },
  goat: {
    label: "Goat", fg: "#d9cab0", bg: "#3e3626",
    art: <>
      <path d="M18 19c0-7-2-11-6-13M29 19c0-7 2-11 6-13" {...stroke} />
      <path d="M16 22c-8-8-13-3-6 2l6 2M32 22c8-8 13-3 6 2l-6 2M24 43l-3-7h6Z" fill="currentColor" />
      <path d="M35 26c0 5-5 14-11 14s-12-8-12-14 5-10 12-10 11 4 11 10Z" fill="currentColor" />
      <circle cx="19" cy="26" r="1.5" fill={INK} /><circle cx="29" cy="26" r="1.5" fill={INK} />
      <path d="m22 33 2 2 2-2" {...line} />
    </>,
  },
  horse: {
    label: "Horse", fg: "#cf9a6c", bg: "#3a2c22",
    art: <>
      <path d="M18 18c-5-8-3-15 0-11l5 9M28 16c3-12 7-13 6-5l-2 9" {...stroke} />
      <path d="M35 25c0 6-2 18-11 18-8 0-12-5-11-11l2-10c1-6 6-9 11-8s9 5 9 11Z" fill="currentColor" />
      <path d="M20 17c-2 3-2 6 0 8" {...line} />
      <circle cx="28" cy="25" r="1.7" fill={INK} />
      <path d="M19 35v1M28 35v1M21 39c2 1 4 1 6-1" {...line} />
    </>,
  },
  duck: {
    label: "Duck", fg: "#f2d66b", bg: "#2b4046",
    art: <>
      <path d="M12 29c-3-9 2-16 9-16 8 0 10 6 9 12 8 1 9 7 6 12-4 7-19 8-24 1l-5-8Z" fill="currentColor" />
      <path d="M29 20h11c2 4-4 7-11 6" fill="currentColor" />
      <circle cx="24" cy="20" r="1.7" fill={INK} />
      <path d="M18 32c0 5 6 7 10 3" {...line} />
    </>,
  },
  goose: {
    label: "Goose", fg: "#e8e4d6", bg: "#344247",
    art: <>
      <path d="M7 27l7 3c3-3 6-4 10-4V16c-2-2-3-4-2-7 1-4 7-6 11-3 3 2 4 6 2 9l-3 3v9c5 2 8 5 7 9-1 5-7 7-15 7-9 0-15-4-17-16Z" fill="currentColor" />
      <path d="m34 10 9 4-10 3" fill="currentColor" />
      <circle cx="30" cy="10" r="1.6" fill={INK} />
      <path d="M15 33c2 5 10 7 15 2M35 14h5" {...line} />
    </>,
  },
  rooster: {
    label: "Rooster", fg: "#e39a5c", bg: "#40292a",
    art: <>
      <path d="M22 16c-6-8-1-11 2-7 3-8 7-6 6 0 6-4 9 0 4 6M14 30c-8-5-10-12-6-14 4-1 7 6 8 10M12 34c-9-1-10-7-6-8" {...stroke} />
      <path d="M35 27c0 11-5 16-14 16-7 0-11-5-10-11 1-5 7-7 8-12 2-8 16-7 16 7Z" fill="currentColor" />
      <path d="m34 22 8 4-8 4" fill="currentColor" />
      <circle cx="29" cy="22" r="1.7" fill={INK} />
      <path d="M18 32c0 5 6 7 10 3" {...line} />
    </>,
  },
  donkey: {
    label: "Donkey", fg: "#a9abb8", bg: "#2d3040",
    art: <>
      <path d="M18 22C10 9 12 1 16 7l6 14M27 21C29 5 34 1 34 9l-3 14" {...stroke} />
      <path d="M36 29c0 8-4 14-12 14S12 38 12 30s4-12 12-12 12 4 12 11Z" fill="currentColor" />
      <circle cx="19" cy="28" r="1.5" fill={INK} /><circle cx="29" cy="28" r="1.5" fill={INK} />
      <path d="M18 35c3-2 9-2 12 0M21 38h.1M27 38h.1" {...line} />
    </>,
  },
  cat: {
    label: "Cat", fg: "#f0a35c", bg: "#3a2f2a",
    art: <>
      <path d="M12 26 11 9c0-3 6 3 11 9M27 18c5-6 10-11 10-7l-1 16" {...stroke} />
      <path d="M38 31c0 8-6 12-14 12S10 39 10 31s6-13 14-13 14 5 14 13Z" fill="currentColor" />
      <circle cx="18" cy="29" r="1.5" fill={INK} /><circle cx="30" cy="29" r="1.5" fill={INK} />
      <path d="m22 34 2 2 2-2M14 34l-7-1M34 34l7-1" {...line} />
    </>,
  },
  dog: {
    label: "Dog", fg: "#d1a77a", bg: "#2f3a3a",
    art: <>
      <path d="M16 20c-6-10-10-3-9 7 1 5 5 5 7 0M32 20c6-10 10-3 9 7-1 5-5 5-7 0" {...stroke} />
      <path d="M36 29c0 8-5 14-12 14s-12-6-12-14 4-13 12-13 12 5 12 13Z" fill="currentColor" />
      <circle cx="19" cy="28" r="1.5" fill={INK} /><circle cx="29" cy="28" r="1.5" fill={INK} />
      <path d="M21 33h6l-3 3Z" fill={INK} />
      <path d="M24 36v2m-4 0q4 3 8 0" {...line} />
    </>,
  },
  barn: {
    label: "Barn", fg: "#e06a5b", bg: "#3a2b2b",
    art: <>
      <path d="M8 24 12 15 24 7 36 15l5 9" {...stroke} />
      <path d="M11 23 24 14l13 9v17c0 2-2 3-4 3H15c-3 0-4-1-4-3Z" fill="currentColor" />
      <path d="M18 42V29h12v13M18 30l12 12M30 30 18 42" {...line} />
      <circle cx="24" cy="23" r="2" fill={INK} />
    </>,
  },
  tractor: {
    label: "Tractor", fg: "#7fcf7a", bg: "#2b3a2c",
    art: <>
      <path d="M14 25V13M25 24V11h12v19M23 10h16" {...stroke} />
      <path d="M7 27c0-3 2-5 5-5h12l4 6h10v8H7Z" fill="currentColor" />
      <circle cx="33" cy="35" r="8" fill="currentColor" /><circle cx="13" cy="37" r="6" fill="currentColor" />
      <circle cx="33" cy="35" r="3" {...line} /><circle cx="13" cy="37" r="2" fill={INK} />
    </>,
  },
};
